import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { Link } from "@tanstack/react-router";
import {
  Table2,
  Database,
  Server,
  FormInput,
  Link2,
  Calculator,
  FileSpreadsheet,
  Globe,
  BarChart3,
  ClipboardList,
  Briefcase,
  Lightbulb,
  ShieldCheck,
  Coins,
  ListChecks,
  LineChart,
  Kanban,
  FolderOpen,
  Zap,
  ShieldAlert,
  ArrowRightLeft,
  Layers,
} from "lucide-react";

import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import MyAppTableTypes from "@/components/marketing/visuals/MyAppTableTypes";
import { MobileStickyCta } from "@/components/marketing/MobileStickyCta";

export const Route = createFileRoute("/products/myapp")({
  component: MyAppPage,
});

const SOFTWARE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Larasoft MyApp",
  description:
    "Build your own small web applications that write directly to a database. Replace spreadsheets and surveys with structured data capture - no per-user fees, pay only for data.",
  url: "https://larasoft.global/products/myapp",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  provider: {
    "@type": "Organization",
    name: "Larasoft",
    url: "https://larasoft.global",
  },
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "ZAR",
    description: "Your first 1,500 records are free.",
  },
};

const METRICS = [
  { label: "To get started", value: "Free" },
  { label: "Per-seat fees", value: "Zero" },
  { label: "SQL access", value: "Direct" },
  { label: "Field types", value: "12+" },
];

const DIFFERENTIATORS = [
  {
    icon: Table2,
    title: "Relational forms",
    description:
      "One form writes to multiple linked tables. Capture header and line items in a single submission - going well beyond what standard form tools offer.",
  },
  {
    icon: ListChecks,
    title: "Central lookup tables",
    description:
      "Define your reference data once - departments, categories, statuses, products - and reuse it across every form. Update in one place and every form stays in sync.",
  },
  {
    icon: Coins,
    title: "Pay for data, not seats",
    description:
      "No per-user fees, ever. Start on the Foundation plan and pay only for the data you store. Invite your whole team without budget anxiety.",
  },
  {
    icon: Database,
    title: "Direct SQL access",
    description:
      "Connect Power BI, Tableau, or anything that speaks SQL. Your data lives in a real database, not a proprietary silo.",
  },
  {
    icon: Server,
    title: "Bring Your Own Database",
    description:
      "Publish to your own Azure SQL or AWS infrastructure. Full control over where your data lives and who accesses it.",
  },
];

const TABLE_TYPES = [
  {
    icon: FormInput,
    title: "Simple tables",
    description:
      "Straightforward record capture with validation, lookups, and calculated fields. Perfect for daily readings, registers, and logs.",
  },
  {
    icon: ClipboardList,
    title: "Header + line items",
    description:
      "Composite forms with a header and detail lines - like an invoice, job card, or purchase order - all in a single submission.",
  },
  {
    icon: ArrowRightLeft,
    title: "Latest-only tables",
    description:
      "Upload a file and it replaces everything. Always current, no clutter. Ideal for monthly data refreshes and reference imports.",
  },
  {
    icon: Layers,
    title: "Version tables",
    description:
      "Import with full history preserved. Every version is kept alongside the current data - great for budgets, forecasts, and audit trails.",
  },
];

const FEATURES = [
  {
    icon: FormInput,
    label:
      "12+ field types - text, numbers, dates, currency, lookups, calculations",
  },
  {
    icon: Link2,
    label:
      "Linked tables with cascading dropdowns and auto-populated fields - select a customer and related details fill in automatically",
  },
  { icon: Calculator, label: "Calculated fields and formulas" },
  {
    icon: Kanban,
    label:
      "Kanban view - drag records through workflow stages like a visual board. Ideal for order tracking, approvals, and task management",
  },
  {
    icon: FolderOpen,
    label:
      "Group related tables into apps - organise your data entry, lookups, and reference tables in one place",
  },
  {
    icon: Zap,
    label:
      "Batch field creation - paste your field names and the system auto-detects types. Add advanced fields individually afterwards",
  },
  {
    icon: ShieldAlert,
    label:
      "Edit protection by default - records are locked after submission. Explicit action required to edit, giving you a built-in audit trail",
  },
  {
    icon: Globe,
    label:
      "Anonymous submission - public forms anyone can complete, no login required. Perfect for surveys, applications, and external data collection",
  },
  {
    icon: ShieldCheck,
    label:
      "Authenticated access - secure areas limited to your team. Control exactly who can view, edit, and submit data",
  },
];

const PERSONAS = [
  {
    icon: FileSpreadsheet,
    title: "Teams moving off spreadsheets",
    description:
      "Your data is scattered across spreadsheets - version conflicts, broken formulas, no audit trail. Move it into a proper database without needing a developer.",
  },
  {
    icon: Briefcase,
    title: "Operations teams",
    description:
      "Order tracking, daily readings, job cards, inspections - structured data capture with workflow stages, without building a full application.",
  },
  {
    icon: BarChart3,
    title: "Power BI / reporting teams",
    description:
      "You want clean, relational source data that connects directly to your dashboards - no more cleaning up spreadsheet exports.",
  },
  {
    icon: Lightbulb,
    title: "Non-technical users",
    description:
      "You think in forms and tables, not code. If you can use a spreadsheet, you can use MyApp.",
  },
];

function MyAppPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>
          MyApp - Your data belongs in a database, not a spreadsheet | Larasoft
        </title>
        <meta
          name="description"
          content="Build your own small web applications that write directly to a database. Replace spreadsheets and surveys with structured data capture - no per-user fees, pay only for data."
        />
        <link rel="canonical" href="https://larasoft.global/products/myapp" />
        <script type="application/ld+json">
          {JSON.stringify(SOFTWARE_SCHEMA)}
        </script>
      </Helmet>

      {/* Hero */}
      <Section
        containerClassName="grid grid-cols-1 items-center gap-8 md:grid-cols-[2fr_3fr]"
        className="py-20"
      >
        <div className="space-y-6">
          <Badge className="w-max" variant="info">
            MyApp
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-brand-blue-dark">
            Your data belongs in a database, not a spreadsheet
          </h1>
          <p className="text-xl font-medium text-brand-blue-dark">
            The flexibility of web forms, without the complication of databases -
            but with all the benefits of one.
          </p>
          <div className="space-y-3 text-slate-600">
            <p>
              Build your own small web applications that write directly to a
              database. Replace multiple spreadsheets and surveys with
              structured, relational data capture - then connect{" "}
              <span className="font-semibold text-brand-blue-dark">
                Power BI, Tableau, or anything that speaks SQL
              </span>{" "}
              when you're ready.
            </p>
            <p>
              Your first 1,500 records are free. No per-user fees - just pay for
              the data you hold on the{" "}
              <span className="font-semibold text-brand-blue-dark">
                Foundation
              </span>{" "}
              plan.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/contact">Start free</Link>
            </Button>
            <Button variant="outline" asChild>
              <a href="#features">See features</a>
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-xs text-slate-600 sm:grid-cols-4">
            {METRICS.map((metric) => (
              <div
                key={metric.label}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
              >
                <div className="text-sm font-semibold text-slate-900">
                  {metric.value}
                </div>
                <div>{metric.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl">
          <MyAppTableTypes />
        </div>
      </Section>

      <MobileStickyCta />

      {/* Key Differentiators */}
      <Section containerClassName="space-y-8" className="py-16 bg-white/50">
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-brand-blue-dark">
            Why teams choose{" "}
            <span className="text-brand-blue-dark">MyApp</span>
          </h2>
          <p className="max-w-3xl text-sm text-slate-600">
            The features that set MyApp apart from spreadsheets and ordinary form
            builders.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DIFFERENTIATORS.map(({ icon: Icon, title, description }) => (
            <Card
              key={title}
              className="h-full border-white/60 bg-white/70 shadow-sm backdrop-blur"
            >
              <CardContent className="space-y-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue-pale/70 text-brand-blue-dark">
                  <Icon className="h-6 w-6" />
                </span>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Table Types */}
      <Section containerClassName="space-y-8" className="py-16">
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-brand-blue-dark">
            Four ways to work with your data
          </h2>
          <p className="max-w-3xl text-sm text-slate-600">
            Capture data through forms, import it from files, or both. Choose the
            table type that fits your workflow.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TABLE_TYPES.map(({ icon: Icon, title, description }) => (
            <Card
              key={title}
              className="h-full border-white/60 bg-white/70 shadow-sm backdrop-blur"
            >
              <CardContent className="space-y-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <Icon className="h-6 w-6" />
                </span>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Features */}
      <Section
        id="features"
        containerClassName="space-y-8"
        className="py-16 bg-white/50 scroll-mt-24"
      >
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-brand-blue-dark">What you can build</h2>
          <p className="max-w-3xl text-sm text-slate-600">
            Create your own small web applications - from anonymous public
            surveys to secure, authenticated team tools - all writing directly to
            a real database.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FEATURES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue-pale/70 text-brand-blue-dark">
                <Icon className="h-5 w-5" />
              </span>
              <span className="text-sm text-slate-700">{label}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Reporting */}
      <Section containerClassName="space-y-8" className="py-16">
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-brand-blue-dark">Reporting that grows with you</h2>
          <p className="max-w-3xl text-sm text-slate-600">
            Start reporting straight away - and graduate to more powerful tools
            when you need them.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Card className="h-full border-white/60 bg-white/70 shadow-sm backdrop-blur">
            <CardContent className="space-y-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue-pale/70 text-brand-blue-dark">
                <BarChart3 className="h-6 w-6" />
              </span>
              <CardTitle>Built-in reporting</CardTitle>
              <CardDescription>
                Get started immediately with our built-in reporting tools - ideal
                for day-to-day operational views of your data, without any extra
                setup or licences.
              </CardDescription>
            </CardContent>
          </Card>
          <Card className="h-full border-white/60 bg-white/70 shadow-sm backdrop-blur">
            <CardContent className="space-y-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue-pale/70 text-brand-blue-dark">
                <LineChart className="h-6 w-6" />
              </span>
              <CardTitle>Grow into Power BI and beyond</CardTitle>
              <CardDescription>
                When you need advanced dashboards, connect Power BI, Tableau, or
                any SQL-compatible tool directly to your database. Use them
                alongside built-in reports or migrate entirely - your data is
                always accessible.
              </CardDescription>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Who It's For */}
      <Section containerClassName="space-y-8" className="py-16 bg-white/50">
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-brand-blue-dark">Who it's for</h2>
          <p className="max-w-3xl text-sm text-slate-600">
            MyApp is designed for teams that think in tables and forms - not
            code.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PERSONAS.map(({ icon: Icon, title, description }) => (
            <Card
              key={title}
              className="h-full border-white/60 bg-white/70 shadow-sm backdrop-blur"
            >
              <CardContent className="space-y-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                  <Icon className="h-6 w-6" />
                </span>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* CTA Band */}
      <Section
        containerClassName="text-center space-y-6"
        className="py-16 bg-gradient-to-r from-brand-blue-dark via-brand-blue-medium to-brand-blue-dark text-white"
      >
        <h2 className="text-2xl sm:text-3xl font-bold">
          Ready to get started?
        </h2>
        <p className="mx-auto max-w-xl text-white/85">
          Move your data out of spreadsheets and into a real database. Your first
          1,500 records are free, and there are never any per-user fees.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            asChild
            className="bg-white text-brand-blue-dark hover:bg-white/90"
          >
            <Link to="/contact">Get in touch</Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-white/60 text-brand-blue-dark bg-white hover:bg-white/90"
          >
            <Link to="/">Explore more products</Link>
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  );
}
